import { ApiResponse } from 'apisauce';

import { APIResponse, GatewayResponse, GatewayResponseStatus, ProtectedRoute } from '~types/gateway';

import { IGoldGateway } from './goldBase';

import {
    TCreateBuyGoldTransactionAPIParams,
    TCreateGoldConversionAPIParams,
    TCreateInventoryAPIParams,
    TCreateTransactionDraftAPIParams,
    TCreateTransactionDraftResponse,
    TExportInventoryTransactionListingAPIResponse,
    TGetExportInventoryTransactionListingAPIParams,
    TGetGoldConversionAmountAPIParams,
    TGetGoldConversionListingAPIParams,
    TGetGoldPricesAPIResponse,
    TGetInventoryFilterAPIParams,
    TGetInventoryFilterAPIResponse,
    TGetInventoryListingAPIParams,
    TGetInventoryListingAPIResponse,
    TGetInventoryTransactionListingAPIParams,
    TGetInventoryTransactionListingAPIResponse,
    TGetProductFilterAPIParams,
    TGetProductFilterAPIResponse,
    TGetProductPricePerGramAPIParams,
    TGetSerialNumberFilterAPIParams,
    TGetSerialNumberFilterAPIResponse,
    TGetSerialNumberListingAPIParam,
    TGetSerialNumberListingData,
    TGetSetialNumberListingAPIResponse,
    TGetTotalNetValueOrQuantityAPIParams,
    TGetTransactionListingAPIParams,
    TGetTransactionListingAPIResponse,
    TGetTransactionReceiptAPIParams,
    TGoldConversionListingAPIResponse,
    TGoldTransactionListing,
    TTransferInventoryAPIParams,
    TCreateEarmarkConversionAPIParams,
    TGetEarmarkConversionListingAPIParams,
    TGetEarmarkConversionListingAPIResponse,
    TUpdateEarmarkDetailsAPIParam,
    TGetInventoryUsageAPIResponse,
    TGetInventoryUsageAPIParams,
    TGetInventoryTransfersAPIParams,
    TGetInventoryTransfersAPIResponse,
} from '~types/feat/gold';

import UtilFunctions from '~utils/functions';
import { TGetInventoryTransferListingAPIParams, TGetInventoryTransferListingAPIResponse } from '~types/feat/setting';

const { API: { getHeaders }, Misc: { simulateApi } } = UtilFunctions;

export default class GoldGateway extends IGoldGateway {
    async getTransactionListing(params: TGetTransactionListingAPIParams): GatewayResponse<TGetTransactionListingAPIResponse> {
        const { authToken, ...restOfGetTransactionListingAPIParams } = params;
        const response: ApiResponse<TGetTransactionListingAPIResponse> = await this.api.get(
            '/transaction/getTransactionList',
            { ...restOfGetTransactionListingAPIParams },
            getHeaders(authToken)
        );
        return this.process(response);
    }

    async getGoldPrices(params: ProtectedRoute): GatewayResponse<TGetGoldPricesAPIResponse> {
        const { authToken } = params;
        const response: ApiResponse<TGetGoldPricesAPIResponse> = await this.api.get(
            '/transaction/getGoldPrices',
            {},
            getHeaders(authToken)
        );
        return this.process(response);
    }

    async createBuyGoldTransaction(params: TCreateBuyGoldTransactionAPIParams): GatewayResponse<string | null> {
        const { authToken, ...restOfCreateBuyGoldTransactionAPIParams } = params;
        const response: ApiResponse<string | null> = await this.api.post(
            '/transaction/createTransaction',
            { ...restOfCreateBuyGoldTransactionAPIParams },
            getHeaders(authToken));
        return this.process(response);
    }

    async createSellGoldTransaction(params: TCreateBuyGoldTransactionAPIParams): GatewayResponse<string | null> {
        const { authToken, ...restOfCreateSellGoldTransactionAPIParams } = params;
        const response: ApiResponse<string | null> = await this.api.post(
            '/transaction/createTransaction',
            { ...restOfCreateSellGoldTransactionAPIParams },
            getHeaders(authToken)
        );
        return this.process(response);
    }

    async getTransactionReceipt(params: TGetTransactionReceiptAPIParams): GatewayResponse<Blob> {
        const { authToken, ...restOfGetTransactionReceiptAPIParams } = params;
        // const response: ApiResponse<Blob> = await this.api.get('/transaction/getTransactionReceipt', params);
        // return this.process(response);

        const response = await simulateApi({
            status: GatewayResponseStatus.Success,
            data: new Blob(['Updated sample content'], { type: 'text/plain' }),
        });

        return response;
    }
    // gold inventory
    async getInventoryTransactionListing(params: TGetInventoryTransactionListingAPIParams): GatewayResponse<TGetInventoryTransactionListingAPIResponse> {
        const { authToken, ...restOfGetInventoryTransactionListingAPIParams } = params;
        const response: ApiResponse<TGetInventoryTransactionListingAPIResponse> = await this.api.get(
            '/inventory/getInventoryTransaction',
            { ...restOfGetInventoryTransactionListingAPIParams },
            getHeaders(authToken)
        );
        return this.process(response);
    }

    async getInventoryListing(params: TGetInventoryListingAPIParams): GatewayResponse<TGetInventoryListingAPIResponse> {
        const { authToken, ...restOfGetInventoryListingAPIParams } = params;
        const response: ApiResponse<TGetInventoryListingAPIResponse> = await this.api.get(
            '/inventory/getInventoryListing',
            { ...restOfGetInventoryListingAPIParams },
            getHeaders(authToken),
        );
        return this.process(response);
    }

    async createInventory(params: TCreateInventoryAPIParams): GatewayResponse<string | null> {
        const { authToken, ...restOfCreateInventoryAPIParams } = params;

        const response: ApiResponse<string | null> = await this.api.post(
            '/inventory/createInventory',
            { ...restOfCreateInventoryAPIParams },
            getHeaders(authToken)
        );

        return this.process(response);
    }

    async transferInventory(params: TTransferInventoryAPIParams): GatewayResponse<string | null> {
        const { authToken, ...restOfTransferInventoryAPIParams } = params;

        const response: ApiResponse<string | null> = await this.apiNoTimeout.post(
            '/inventory/transferInventory',
            { ...restOfTransferInventoryAPIParams },
            getHeaders(authToken)
        );

        return this.process(response);
    }

    async exportInventoryTransactionListing(params: TGetExportInventoryTransactionListingAPIParams): GatewayResponse<TExportInventoryTransactionListingAPIResponse> {
        const { authToken, ... restOfExportInventoryTransactionListingAPIParams } = params;
        const response: ApiResponse<TExportInventoryTransactionListingAPIResponse> = await this.api.get(
            '/inventory/exportInventoryTransaction',
            { ...restOfExportInventoryTransactionListingAPIParams },
            getHeaders(authToken)
        );
        return this.process(response);
    }

    async exportGoldTransactionListing(params: ProtectedRoute): GatewayResponse<TGoldTransactionListing[]> {
        const { authToken, ...restOfExportListingFilterParams } = params;
        const response: ApiResponse<TGoldTransactionListing[]> = await this.api.get('/transaction/exportTransactionListing', { ...restOfExportListingFilterParams }, getHeaders(authToken));
        return this.process(response);
    }

    async exportGoldTransactionListingNoTimeout(params: ProtectedRoute): GatewayResponse<TGoldTransactionListing[]> {
        const { authToken, ...restOfExportListingFilterParams } = params;
        const response: ApiResponse<TGoldTransactionListing[]> = await this.apiNoTimeout.get(
            '/transaction/exportTransactionListing',
            { ...restOfExportListingFilterParams },
            getHeaders(authToken)
        );
        return this.process(response);
    }

    //others
    async getAllProducts(params: TGetProductFilterAPIParams): GatewayResponse<TGetProductFilterAPIResponse> {
        const { authToken, ...restOfGetProductFilterAPIParams } = params;
        const response: ApiResponse<TGetProductFilterAPIResponse> = await this.api.get(
            '/product/getProductFilter',
            { ...restOfGetProductFilterAPIParams },
            getHeaders(authToken)
        );
        return this.process(response);
    }

    async getAllInventories(params: TGetInventoryFilterAPIParams): GatewayResponse<TGetInventoryFilterAPIResponse> {
        const { authToken, ...restOfGetInventoryFilterAPIParams } = params;
        const response: ApiResponse<TGetInventoryFilterAPIResponse> = await this.api.get(
            '/inventory/getTotalInventory',
            { ...restOfGetInventoryFilterAPIParams },
            getHeaders(authToken)
        );
        return this.process(response);
    }

    async getAllSerialNumbers(params: TGetSerialNumberFilterAPIParams): GatewayResponse<TGetSerialNumberFilterAPIResponse> {
        const { authToken, ...restOfGetSerialNumberFilterAPIParams } = params;
        const response: ApiResponse<TGetSerialNumberFilterAPIResponse> = await this.api.get(
            '/transaction/getSerialNumberFilter',
            { ...restOfGetSerialNumberFilterAPIParams },
            getHeaders(authToken)
        );
        return this.process(response);
    }

    async getTotalNetValueOrQuantity(params: TGetTotalNetValueOrQuantityAPIParams): GatewayResponse<string | null> {
        const { authToken, ...restOfAPIParams } = params;
        const response: ApiResponse<string | null> = await this.api.get(
            '/transaction/getTransactionNetValue',
            { ...restOfAPIParams },
            getHeaders(authToken)
        );
        return this.process(response);
    }

    async createTransactionDraft(params: TCreateTransactionDraftAPIParams): GatewayResponse<TCreateTransactionDraftResponse> {
        const { authToken, ...restOfAPIParams } = params;
        const response: ApiResponse<TCreateTransactionDraftResponse> = await this.api.post(
            '/transaction/createTransactionDraft',
            { ...restOfAPIParams },
            getHeaders(authToken)
        );
        return this.process(response);
    }

    async getTransactionPricePerGramValue(params: TGetProductPricePerGramAPIParams): GatewayResponse<string | null> {
        const { authToken, ...restOfAPIParams } = params;
        const response: ApiResponse<string | null> = await this.api.get(
            '/transaction/getTransactionPricePerGramValue',
            { ...restOfAPIParams },
            getHeaders(authToken)
        );
        return this.process(response);
    }

    async getInventoryTransferListing(params: TGetInventoryTransferListingAPIParams): GatewayResponse<TGetInventoryTransferListingAPIResponse> {
        const { authToken, ...restOfAPIParams } = params;
        const response: ApiResponse<TGetInventoryTransferListingAPIResponse> = await this.api.get(
            '/inventory/getInventoryTransferListing',
            restOfAPIParams,
            getHeaders(authToken)
        );
        return this.process(response);
    }

    async getSerialNumberListing(params: TGetSerialNumberListingAPIParam): GatewayResponse<TGetSerialNumberListingData[]> {
        const { authToken, ...restOfGetSerialNumberListingAPIParams } = params;
        const response: ApiResponse<TGetSerialNumberListingData[]> = await this.api.get(
            '/inventory/getSerialNumberListing',
            { ...restOfGetSerialNumberListingAPIParams },
            getHeaders(authToken),
        );
        return this.process(response);
    }

    async getGoldInventoryReportListing(params: TGetInventoryTransactionListingAPIParams): GatewayResponse<TGetInventoryTransactionListingAPIResponse> {
        const { authToken, ...restOfGetInventoryTransactionListingAPIParams } = params;
        const response: ApiResponse<TGetInventoryTransactionListingAPIResponse> = await this.api.get(
            '/inventory/getInventoryTransaction',
            { ...restOfGetInventoryTransactionListingAPIParams },
            getHeaders(authToken)
        );
        return this.process(response);
    }

    async getBranchInventoryListing(params: TGetInventoryListingAPIParams): GatewayResponse<TGetInventoryListingAPIResponse> {
        const { authToken, ...restOfGetInventoryListingAPIParams } = params;
        const response: ApiResponse<TGetInventoryListingAPIResponse> = await this.api.get(
            '/inventory/getInventoryListing',
            { ...restOfGetInventoryListingAPIParams },
            getHeaders(authToken),
        );
        return this.process(response);
    }

    // gold conversion
    async createGoldConversion(params: TCreateGoldConversionAPIParams): GatewayResponse<string | null> {
        const { authToken, ...restOfCreateInventoryAPIParams } = params;

        const response: ApiResponse<string | null> = await this.api.post(
            '/conversion/createConversion',
            { ...restOfCreateInventoryAPIParams },
            getHeaders(authToken)
        );

        return this.process(response);
    }

    async getGoldConversionListing(params: TGetGoldConversionListingAPIParams): GatewayResponse<TGoldConversionListingAPIResponse> {
        const { authToken, ...restOfGetInventoryListingAPIParams } = params;
        const response: ApiResponse<TGoldConversionListingAPIResponse> = await this.api.get(
            '/conversion/getConversionListing',
            { ...restOfGetInventoryListingAPIParams },
            getHeaders(authToken),
        );
        return this.process(response);
    }

    async getGoldConversionAmount(params: TGetGoldConversionAmountAPIParams): GatewayResponse<string | null> {
        const { authToken, ...restOfGetInventoryListingAPIParams } = params;
        const response: ApiResponse<string | null> = await this.api.get(
            '/conversion/getConversionAmount',
            { ...restOfGetInventoryListingAPIParams },
            getHeaders(authToken),
        );
        return this.process(response);
    }

    async createEarmarkConversion(params: TCreateEarmarkConversionAPIParams): GatewayResponse<string | null> {
        const { authToken, ...restOfCreateEarmarkConversionAPIParams } = params;
        const response: ApiResponse<string | null> = await this.api.post(
            '/transaction/createEarmarkConversion',
            { ...restOfCreateEarmarkConversionAPIParams },
            getHeaders(authToken)
        );
        return this.process(response);
    }

    async getEarmarkConversionListing(params: TGetEarmarkConversionListingAPIParams): GatewayResponse<TGetEarmarkConversionListingAPIResponse> {
        const { authToken, ...restOfParams } = params;
        const response: ApiResponse<TGetEarmarkConversionListingAPIResponse> = await this.api.get(
            '/transaction/getEarmarkConversionList',
            { ...restOfParams },
            getHeaders(authToken)
        );
        return this.process(response);
    }

    async updateEarmarkDetails(params: TUpdateEarmarkDetailsAPIParam): GatewayResponse<string | null> {
        const { authToken, ...restOfParams } = params;
        const response: ApiResponse<string | null> = await this.api.put(
            'transaction/updateEarmarkDetails',
            { ...restOfParams },
            getHeaders(authToken)
        );
        return this.process(response);
    }

    async getInventoryUsage(params: TGetInventoryUsageAPIParams): GatewayResponse<TGetInventoryUsageAPIResponse> {
        const { authToken, ...restOfParams } = params;
        const response: ApiResponse<TGetInventoryUsageAPIResponse> = await this.api.get(
            '/inventory/getInventoryUsage',
            { ...restOfParams },
            getHeaders(authToken)
        );
        return this.process(response);
    }

    async getInventoryTransfers(params: TGetInventoryTransfersAPIParams): GatewayResponse<TGetInventoryTransfersAPIResponse> {
        const { authToken, ...restOfParams } = params;
        const response: ApiResponse<TGetInventoryTransfersAPIResponse> = await this.api.get(
            '/inventory/getInventoryTransfers',
            { ...restOfParams },
            getHeaders(authToken)
        );
        return this.process(response);
    }
}
